import React, { useEffect, useRef } from 'react';
import './SectionChips.css';

/**
 * Section chips over the jot bar (2026-09-25): tap where in the song the
 * student is, then jot. The choice sticks until another chip is tapped, so a
 * run of notes on the chorus is one tap, not one per note. "Whole song" is the
 * default and is what every earlier note carries.
 *
 * One scrolling row (never wraps, so the pinned bar's height is fixed) with the
 * active chip scrolled into view. A trailing control opens the sections sheet
 * to rename or detect again. Everything is always rendered, 40px targets, no
 * hover-only affordance (CLAUDE.md).
 */
const SectionChips = ({ sections = [], activeId = null, onSelect, status = null, isGeneric = false, onManage = null }) => {
	const rowRef = useRef(null);

	useEffect(() => {
		const row = rowRef.current;
		const active = row ? row.querySelector('.ld-secchip.is-active') : null;
		// scrollLeft, not scrollIntoView: the row must never scroll the PAGE.
		if (row && active) row.scrollLeft = Math.max(0, active.offsetLeft - 14);
	}, [activeId]);

	if (!sections.length) return null;
	const chip = (id, label) => (
		<button
			key={id || 'whole'}
			type="button"
			className={`ld-secchip${(activeId || null) === id ? ' is-active' : ''}`}
			aria-pressed={(activeId || null) === id}
			onClick={() => onSelect(id)}>
			{/* Pass 3 D4: the selected section says so with a check, not colour
			    alone, and the lyric labels carry the same mark. */}
			{(activeId || null) === id && <i className="fa-sharp fa-solid fa-check ld-secchip__check" aria-hidden="true" />}
			{label}
		</button>
	);

	return (
		<div className="ld-sectionchips" role="group" aria-label="Song section for new notes">
			<div className="ld-sectionchips__row" ref={rowRef}>
				{chip(null, 'Whole song')}
				{sections.map((s) => chip(s.id, s.label))}
				{status === 'detecting' && (
					<span className="ld-sectionchips__status" role="status">
						<i className="fa-sharp fa-solid fa-rotate fa-spin" aria-hidden="true" /> Finding sections
					</span>
				)}
			</div>
			{onManage && (
				<button
					type="button"
					className="ld-sectionchips__manage"
					aria-label={isGeneric ? 'About song sections' : 'Edit song sections'}
					onClick={onManage}>
					<i className="fa-sharp fa-solid fa-sliders" aria-hidden="true" />
				</button>
			)}
		</div>
	);
};

export default SectionChips;
